// Import directly from `./catalogue` (not `./index`) to avoid a
// circular dependency: `index.ts` re-exports `validateMcpFlagSet` from
// this module, so importing the catalogue back via `./index` would
// create `index → validateMcpFlagSet → index`.
import { isMcpFlag, type McpFlag } from './catalogue';

/**
 * Failure shapes for `validateMcpFlagSet`.
 *
 * - `not-an-array` — the input is not an array (e.g. an object/string left in
 *   the JSONB column by a manual edit). Treated as a hard boundary failure.
 * - `unknown-flag` — an array element that is not a known MCP flag. The
 *   `field` argument supplied by the caller is echoed back so the message
 *   can name which array (e.g. `enable`, `disable`, `current`) was malformed.
 */
export type ValidateMcpFlagSetFailure =
  | { readonly kind: 'not-an-array'; readonly field: string; readonly value: unknown }
  | {
      readonly kind: 'unknown-flag';
      readonly field: string;
      readonly value: unknown;
    };

export type ValidateMcpFlagSetResult =
  | { readonly ok: true; readonly flags: readonly McpFlag[] }
  | { readonly ok: false; readonly failures: readonly ValidateMcpFlagSetFailure[] };

/**
 * Strictly validate a flag-set array against the MCP catalogue.
 *
 * - The input must be an array; non-arrays produce a `not-an-array` failure.
 * - Every element must be a known MCP flag; unknown values (including
 *   non-strings) produce an `unknown-flag` failure whose `value` echoes the
 *   original input.
 * - Duplicates are collapsed, preserving first-seen order.
 * - An empty array is valid and returns an empty `flags` list.
 *
 * Use this at boundaries that accept a flag-set OUTSIDE the
 * read/validate/write delta path — for example, validating an inbound
 * request body's `enable` array independently, or asserting in a test
 * that a stored `available_features` snapshot is well-formed.
 *
 * Note: `applyMcpFlagDelta` already boundary-validates its own
 * `current` argument and surfaces a non-array as `invalid-current-set`
 * (and a pre-existing orphan as `missing-parent path: 'current'`), so
 * the route/CLI write paths do NOT need to call `validateMcpFlagSet`
 * first.
 */
export function validateMcpFlagSet(input: unknown, field: string): ValidateMcpFlagSetResult {
  if (!Array.isArray(input)) {
    return {
      ok: false,
      failures: [{ kind: 'not-an-array', field, value: input }],
    };
  }

  const flags: McpFlag[] = [];
  const failures: ValidateMcpFlagSetFailure[] = [];
  const seen = new Set<McpFlag>();

  for (const value of input) {
    if (!isMcpFlag(value)) {
      failures.push({ kind: 'unknown-flag', field, value });
      continue;
    }
    if (seen.has(value)) continue;
    seen.add(value);
    flags.push(value);
  }

  if (failures.length > 0) {
    return { ok: false, failures };
  }
  return { ok: true, flags };
}

// Re-export the catalogue list so callers that build error messages from
// `validateMcpFlagSet` failures don't have to import from two paths.
export { MCP_FLAG_VALUES } from './catalogue';
